import React from 'react';
import dayjs from 'dayjs';
import { Badge, Button, Modal } from 'react-bootstrap';
import { ITransaction } from './types';

/**
 * v49 fix-7: popup для просмотра деталей транзакции из Transactions/Поиск.
 * Аналог детальной карточки AML (AmlCaseDetail) — единый стиль.
 */

interface Props {
  show: boolean;
  transaction: ITransaction | null;
  onHide: () => void;
}

// v50 hotfix: backend может вернуть trxId как number (BIGINT в Go-сервисе),
// а тип ITransaction объявляет string → runtime крашит на .slice(). Защита —
// приводим к строке + null-guard.
function shortHash(s?: string | number | null, head = 10, tail = 8): string {
  if (s === null || s === undefined || s === '') return '—';
  const str = String(s);
  if (str.length <= head + tail + 1) return str;
  return `${str.slice(0, head)}…${str.slice(-tail)}`;
}

function formatDate(iso?: string): string {
  if (!iso || iso === '0001-01-01T00:00:00Z') return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD.MM.YYYY HH:mm:ss') : iso;
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="row g-2 mb-2">
    <div className="col-4 text-muted small">{label}</div>
    <div className="col-8" style={{ wordBreak: 'break-all' }}>{value}</div>
  </div>
);

const TransactionDetailModal: React.FC<Props> = ({ show, transaction, onHide }) => {
  if (!transaction) return null;
  const t = transaction;
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          Транзакция <code className="ms-2" style={{ fontSize: '0.85rem' }}>{shortHash(t.trxId, 12, 8)}</code>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row label="ID транзакции" value={<code>{t.trxId || '—'}</code>} />
        <Row
          label="Сумма"
          value={<><strong>{t.amount}</strong> <span className="text-muted">{t.currency}</span></>}
        />
        <Row label="Комиссия" value={<span>{t.fee}%</span>} />
        <Row label="Тип / Статус" value={<><Badge bg="light" text="dark">{t.kind || '—'}</Badge>{' '}<span className="text-muted">{t.status || ''}</span></>} />
        <Row label="Отправитель" value={<><strong>{t.accountOutName || '—'}</strong><div className="text-muted small font-monospace">{t.accountOut}</div></>} />
        <Row label="Получатель" value={<><strong>{t.accountInName || '—'}</strong><div className="text-muted small font-monospace">{t.accountIn}</div></>} />
        <Row label="Начало операции" value={formatDate(t.start_date)} />
        <Row label="Обновлено" value={formatDate(t.update_date)} />
        <Row label="Комментарий" value={t.description || '—'} />
        {t.amlTriggers && t.amlTriggers.length > 0 && (
          <>
            <hr />
            <div className="fw-semibold mb-2">Сработавшие AML-сценарии ({t.amlTriggers.length})</div>
            {t.amlTriggers.map((trg) => (
              <div key={trg.scenarioId} className="border rounded p-2 mb-2 small">
                <div className="d-flex gap-2 align-items-center mb-1">
                  <code style={{ fontSize: '0.78rem' }}>{trg.scenarioCode}</code>
                  <Badge bg="warning" text="dark">риск {trg.riskContribution}</Badge>
                </div>
                <div className="fw-semibold">{trg.scenarioName}</div>
                <div className="text-muted">{trg.criteriaDescription}</div>
                <div className="text-muted" style={{ fontSize: '0.72rem' }}>{formatDate(trg.triggeredAt)}</div>
              </div>
            ))}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onHide}>Закрыть</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TransactionDetailModal;
